import { ref, computed, onMounted } from 'vue'
import brand from '@/brand'

// Cookie consent preferences
const preferences = ref({
  necessary: true, // Always true, cannot be disabled
  analytics: false,
  marketing: false
})

// Consent state
const hasConsent = ref(false)
const consentTimestamp = ref(null)

// Cookie management utility functions
const CONSENT_COOKIE_NAME = brand.cookieConsentName
const CONSENT_EXPIRY_DAYS = 365

/**
 * Set a cookie with proper security attributes
 */
const setCookie = (name, value, days = CONSENT_EXPIRY_DAYS) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000))
  
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const sameSite = '; SameSite=Strict'
  
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/${secure}${sameSite}`
}

/**
 * Get a cookie value
 */
const getCookie = (name) => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

/**
 * Delete a cookie
 */
const deleteCookie = (name) => {
  if (!name || name.trim() === '') return
  
  // Standard deletion
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  
  // Try with different domain variations for thorough cleanup
  const hostname = window.location.hostname
  
  // Only try domain variants for non-localhost environments
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname}`
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname}`
  }
}

/**
 * Delete all non-necessary cookies based on current preferences
 */
const cleanupCookies = () => {
  // List of known tracking/analytics cookies to remove
  const trackingCookies = [
    '_ga', '_gid', '_gat', '_gtag', '_gtm',
    'fbp', '_fbp', 'fr',
    '_hjid', '_hjFirstSeen', '_hjIncludedInPageviewSample',
    '__utma', '__utmb', '__utmc', '__utmt', '__utmz',
    '_dc_gtm_', '_gcl_au', '_gcl_aw'
  ]

  // Remove analytics cookies if not consented
  if (!preferences.value.analytics) {
    trackingCookies.forEach(cookieName => {
      deleteCookie(cookieName)
    })
    
    // Clear Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      })
    }
    
    // Clear any existing GA data
    if (window.ga) {
      window.ga('remove')
    }
  }

  // Remove marketing cookies if not consented
  if (!preferences.value.marketing) {
    // Additional marketing-specific cookies
    const marketingCookies = [
      'IDE', 'test_cookie', 'DSID', 'FLC', 'AID', 'TAID',
      '__Secure-3PAPISID', '__Secure-3PSID', '__Secure-3PSIDCC'
    ]
    
    marketingCookies.forEach(cookieName => {
      deleteCookie(cookieName)
    })
    
    // Update Google consent mode for marketing
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      })
    }
  }
}

/**
 * Initialize tracking scripts based on consent
 */
const initializeTracking = () => {
  // Initialize Google Analytics if analytics consent is given
  if (preferences.value.analytics) {
    // Initialize Google Analytics
    if (!window.gtag && !document.querySelector('script[src*="googletagmanager"]')) {
      // Load Google Analytics
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID' // Replace with actual GA ID
      document.head.appendChild(script)
      
      window.dataLayer = window.dataLayer || []
      function gtag(){dataLayer.push(arguments)}
      window.gtag = gtag
      gtag('js', new Date())
      gtag('config', 'GA_MEASUREMENT_ID') // Replace with actual GA ID
    }
    
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      })
    }
  }
  
  // Initialize marketing tracking if marketing consent is given
  if (preferences.value.marketing) {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      })
    }
    
    // Initialize other marketing tools here (Facebook Pixel, etc.)
  }
}

/**
 * Save consent preferences
 */
const saveConsent = () => {
  const consentData = {
    preferences: preferences.value,
    timestamp: new Date().toISOString(),
    version: '1.0'
  }
  
  setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consentData))
  hasConsent.value = true
  consentTimestamp.value = consentData.timestamp
  
  // Clean up unwanted cookies and initialize allowed tracking
  cleanupCookies()
  initializeTracking()
}

/**
 * Load existing consent preferences
 */
const loadConsent = () => {
  const consentCookie = getCookie(CONSENT_COOKIE_NAME)
  
  if (consentCookie) {
    try {
      const consentData = JSON.parse(consentCookie)
      preferences.value = { ...preferences.value, ...consentData.preferences }
      hasConsent.value = true
      consentTimestamp.value = consentData.timestamp
      
      // Initialize tracking based on saved preferences
      initializeTracking()
      return true
    } catch (error) {
      console.error('Error parsing consent cookie:', error)
      deleteCookie(CONSENT_COOKIE_NAME)
    }
  }
  
  return false
}

// Computed property to determine if consent banner should be shown
const showConsent = computed(() => !hasConsent.value)

// Main composable function
export function useCookieConsent() {
  /**
   * Accept all cookies
   */
  const acceptAll = () => {
    preferences.value = {
      necessary: true,
      analytics: true,
      marketing: true
    }
    saveConsent()
  }

  /**
   * Accept only necessary cookies
   */
  const acceptNecessaryOnly = () => {
    preferences.value = {
      necessary: true,
      analytics: false,
      marketing: false
    }
    saveConsent()
  }

  /**
   * Save current preferences
   */
  const savePreferences = () => {
    saveConsent()
  }

  /**
   * Revoke consent and clear all cookies
   */
  const revokeConsent = () => {
    deleteCookie(CONSENT_COOKIE_NAME)
    preferences.value = {
      necessary: true,
      analytics: false,
      marketing: false
    }
    hasConsent.value = false
    consentTimestamp.value = null
    cleanupCookies()
  }

  /**
   * Check if a specific cookie category is allowed
   */
  const isAllowed = (category) => {
    return preferences.value[category] === true
  }

  /**
   * Update consent preferences (for settings page)
   */
  const updatePreferences = (newPreferences) => {
    preferences.value = { ...preferences.value, ...newPreferences }
    saveConsent()
  }

  // Initialize on mount
  onMounted(() => {
    // Set default consent mode before loading
    if (window.gtag) {
      window.gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      })
    }
    
    // Load existing consent
    loadConsent()
  })

  return {
    preferences,
    hasConsent,
    consentTimestamp,
    showConsent,
    acceptAll,
    acceptNecessaryOnly,
    savePreferences,
    revokeConsent,
    isAllowed,
    updatePreferences
  }
}
